import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from './firebase';

export interface LeadFormRef {
  formId: string;
  name: string;
  developmentId?: string;
}

/**
 * Get the embed base URL for lead forms based on the current environment.
 * Forms are hosted by the main PropCloudAI app.
 */
export function getFormEmbedBaseUrl(): string {
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
  if (projectId === 'propcloudai') {
    return 'https://propcloudai.web.app';
  }
  return 'https://propcloudai-dev.web.app';
}

/**
 * Find a published lead form linked to a specific development.
 */
export async function getLeadFormForDevelopment(developmentId: string): Promise<LeadFormRef | null> {
  try {
    const q = query(
      collection(db, 'leadForms'),
      where('developmentId', '==', developmentId),
      where('isPublished', '==', true),
      limit(1)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    const doc = snapshot.docs[0];
    const data = doc.data();
    return {
      formId: data.formId,
      name: data.name,
      developmentId: data.developmentId,
    };
  } catch (error) {
    console.error('Error fetching lead form for development:', error);
    return null;
  }
}

/**
 * Find a published newsletter lead form.
 */
export async function getNewsletterForm(): Promise<LeadFormRef | null> {
  try {
    const q = query(
      collection(db, 'leadForms'),
      where('formType', '==', 'newsletter_form'),
      where('isPublished', '==', true),
      limit(1)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    const doc = snapshot.docs[0];
    const data = doc.data();
    return {
      formId: data.formId,
      name: data.name,
    };
  } catch (error) {
    console.error('Error fetching newsletter form:', error);
    return null;
  }
}

/**
 * Find a published general lead form (no developmentId, not newsletter).
 * Only returns forms that are NOT tied to a specific development.
 * This prevents showing the wrong development's form as a fallback.
 */
export async function getGeneralLeadForm(): Promise<LeadFormRef | null> {
  try {
    const q = query(
      collection(db, 'leadForms'),
      where('isPublished', '==', true),
      where('formType', '==', 'lead_form'),
      limit(10)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;

    // Only return a form that has no developmentId (truly general purpose)
    const generalForm = snapshot.docs.find(d => !d.data().developmentId);
    if (!generalForm) return null;

    const data = generalForm.data();
    return {
      formId: data.formId,
      name: data.name,
    };
  } catch (error) {
    console.error('Error fetching general lead form:', error);
    return null;
  }
}
